import { create } from "zustand";
import type { PlatformUser } from "@/types";
import { authService } from "@/services/auth.service";

type AuthStatus = "bootstrapping" | "idle" | "loading" | "error";

type AuthState = {
  token: string | null;
  user: PlatformUser | null;
  status: AuthStatus;
  initialized: boolean;
  error: string | null;
  mfaChallengeToken: string | null;
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<{ mfaRequired: boolean }>;
  completeMfaLogin: (code: string) => Promise<void>;
  setUser: (user: PlatformUser | null) => void;
  setSession: (accessToken: string, user?: PlatformUser) => void;
  logout: () => void;
};

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  user: null,
  status: "bootstrapping",
  initialized: false,
  error: null,
  mfaChallengeToken: null,
  initialize: async () => {
    if (get().initialized) return;
    set({ status: "bootstrapping", error: null });
    try {
      const response = await authService.refresh();
      set({
        token: response.accessToken,
        user: response.user,
        status: "idle",
        initialized: true,
      });
    } catch {
      set({
        token: null,
        user: null,
        status: "idle",
        initialized: true,
      });
    }
  },
  login: async (email, password) => {
    set({ status: "loading", error: null });
    try {
      const response = await authService.login({ email, password });
      if ("mfaRequired" in response) {
        set({
          status: "idle",
          initialized: true,
          mfaChallengeToken: response.challengeToken,
        });
        return { mfaRequired: true };
      }
      set({
        token: response.accessToken,
        user: response.user,
        status: "idle",
        initialized: true,
        mfaChallengeToken: null,
      });
      return { mfaRequired: false };
    } catch (error) {
      set({ status: "error", error: "Invalid credentials", initialized: true });
      throw error;
    }
  },
  completeMfaLogin: async (code) => {
    const challengeToken = get().mfaChallengeToken;
    if (!challengeToken) throw new Error("MFA challenge is missing");
    set({ status: "loading", error: null });
    try {
      const response = await authService.completeMfaLogin(challengeToken, code);
      set({
        token: response.accessToken,
        user: response.user,
        status: "idle",
        initialized: true,
        mfaChallengeToken: null,
      });
    } catch (error) {
      set({ status: "error", error: "Invalid authenticator code" });
      throw error;
    }
  },
  setUser: (user) => set({ user }),
  setSession: (accessToken, user) =>
    set((state) => ({
      token: accessToken,
      user: user ?? state.user,
      initialized: true,
      status: "idle",
    })),
  logout: () => {
    set({
      token: null,
      user: null,
      status: "idle",
      initialized: true,
      error: null,
      mfaChallengeToken: null,
    });
    void authService.logout().catch(() => undefined);
  },
}));
